import { Investors } from '@components/book-trial-class-jp/investors'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { PygameSeo } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const CourseCard = dynamic(() =>
  import('@components/course-landing-page/course-card')
)

const { PYGAME } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['pc'].trialCourseQuery}`

const Pygame = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 600)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...PygameSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[PYGAME].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="Top Coding Class Instructors for Kids and Teens to Learn Pygame"
        description="Trinket is like a magical playground where kids can bring their ideas to life through code. Using simple blocks and Python’s Turtle module, they’ll create drawings, fun animations, and even their own games—just like real programmers! Whether they’re designing a house, a caterpillar, or colorful shapes, kids will see their creations instantly come alive on screen. Trinket is completely browser-based and easy to use, making it perfect for beginners. As they explore, kids build a strong foundation in Python while developing creativity, logic, and problem-solving skills in a fun, visual, and interactive way."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={PYGAME}
        urlQuery={urlQuery}
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={PYGAME} />
          <RecommendedCourses
            courseName={PYGAME}
            courseCodes={['pc', 'cgm']}
            title="Our Pygame class curriculum"
            description={
              <Trans>
                Our Pygame curriculum is designed to guide students through
                every step of game development using Python. Starting with the
                basics, students will learn to set up their development
                environment, create interactive game screens, and work with
                visual elements like images, backgrounds, and text. As the
                course progresses, they will dive into more complex topics like
                adding sprites, collision detection, and custom events, building
                up to creating their own full-fledged games.
                <br />
                With a hands-on approach, students will apply their skills to
                create engaging games like Space Invader, learning essential
                game-building concepts along the way. By the end of the course,
                students will have developed problem-solving skills, mastered
                key game development techniques, and gained the confidence to
                bring their own creative ideas to life using Python & Pygame.
              </Trans>
            }
          />
          <Activities courseName={PYGAME} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked coding tutors
                ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                All our instructors have a background in Computer Science and
                undergo rigorous training, contributing to our outstanding NPS
                score of 86. Teachers receive a rating of 4.9 out of 5 from
                students and parents, while the curriculum content is rated 4.8
                out of 5. With a teacher ratio of 91% female to 9% male, we
                provide a comfortable learning environment. Our tutors
                understand students&#39; needs and offer personalized attention,
                even in group classes. Additionally, students have access to
                24/7 doubt resolution with an expert tutor whenever they need
                assistance.
              </Trans>
            }
          />
          <CourseFeatures
            courseTitle={PYGAME}
            title={<Trans>An engaging Pygame programming class for kids</Trans>}
          />

          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we create an ideal environment for kids to master
                  Pygame from the ground up. Our thoughtfully designed
                  curriculum combines real-world tools, creative projects, and
                  interactive learning to ensure a comprehensive and enjoyable
                  learning experience.
                </span>
              </Trans>
            }
            courseName={PYGAME}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                We are proud to offer STEM.org-accredited certificates as part
                of our Python course, specifically tailored to middle and high
                school students eager to explore game development through
                Pygame. At Codingal, our STEM.org-accredited curriculum blends
                Science, Technology, Engineering, and Mathematics seamlessly
                into coding and game design.
                <br />
                For example, in the Pygame module, students not only learn to
                code but also apply mathematical and logical concepts to create
                exciting games like Space Invaders or a racing simulator. They
                explore how physics principles like collision detection or
                motion are implemented in games, combining technology with
                real-world applications.
                <br />
                Upon completing the Pygame module, students earn a
                STEM-accredited certificate, which they can proudly share on
                social media, with mentors, or as part of their learning
                portfolio. This certification showcases their coding skills and
                creativity, giving them an advantage in university applications
                and future career opportunities.
                <br />
                STEM.org, a US-based research and credentialing organization,
                ensures our curriculum meets the highest standards in STEM
                education. Through our Pygame module, students gain valuable
                problem-solving and logical thinking skills, setting them up for
                success in game development and beyond.
              </>
            }
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview shouldScrollToTop isCoursepage />
          <CodingForStudents
            title="Our Pygame classes are appropriate for middle school & High school students"
            description={
              <>
                At Codingal, our Pygame classes are specifically designed for
                middle and high school students, with a clear and easy-to-follow
                course progression. We begin with fun, beginner-level concepts
                that help kids grasp the basics of coding in a simple and
                engaging way. As they become more confident, they advance to
                more complex topics, gradually building a strong foundation in
                programming. This structured approach ensures that students
                develop the skills they need to tackle more challenging projects
                and programming languages as they continue their coding journey.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <Faq courseName={PYGAME} title="Frequently Asked Questions" />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses
            courseCodes={['aiepc', 'aigc', 'rc', 'sp', 'pc', 'cgm']}
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={PYGAME}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Pygame for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Pygame
